import {
  AfterContentChecked,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'craft-input',
  template: `<input
    #input
    [class]="className"
    type="text"
    [disabled]="disabled"
    [attr.maxlength]="maxlength"
    [placeholder]="placeholder"
    (keydown.enter)="onKeydownEnter($event)"
    (keydown.esc)="onKeydownEsc($event)"
    [value]="value"
    (focus)="onFocus($event)"
    (blur)="onBlur($event)"
    (input)="onInput($event.target)"
  />`,
  styleUrls: ['./craft-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CraftInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CraftInputComponent implements ControlValueAccessor, AfterContentChecked {
  private originValue = '';

  @Input() public placeholder = '';
  @Input() public className: string;
  @Input() public maxlength: number;
  @Input() public focusAfterInit = false;
  @Input() public allowEmptyOnBlur = false;

  @Output() public esc = new EventEmitter<string>();
  @Output() public enter = new EventEmitter<string>();

  @ViewChild('input', { static: true }) public inputRef: ElementRef;

  public value = '';
  public disabled = false;

  public onChange = (_: any) => {};
  public onTouched = () => {};

  constructor() {}

  public ngAfterContentChecked() {
    if (this.focusAfterInit) {
      const input = this.inputRef.nativeElement as HTMLInputElement;
      input?.focus();
    }
  }

  public onKeydownEnter(event: Event) {
    event.preventDefault();
    this.commitChanges(this.value);
    this.enter.next(this.value);
  }

  public onKeydownEsc(event: Event) {
    event.preventDefault();
    this.rollbackValue();
    this.esc.next(this.value);
  }

  public onFocus(event: FocusEvent) {
    this.originValue = this.value;
  }

  public onBlur(event: FocusEvent) {
    this.onTouched();
    if (!this.value && !this.allowEmptyOnBlur) {
      this.rollbackValue();
    }
  }

  public onInput(target: any) {
    this.value = target?.value;
    this.onChange(this.value);
  }

  public writeValue(value: any): void {
    this.value = value || '';
    this.originValue = value;
  }

  public registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private rollbackValue() {
    this.value = this.originValue;
    this.onChange(this.value);
  }

  private commitChanges(value: string) {
    this.value = value || this.originValue;
    this.originValue = this.value;
    this.onChange(this.value);
  }
}
